import { Plus, X } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { FaqItem } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useProductScope } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { pendingFaqSuggestions } from './faq-suggestions';

export const MAX_FAQ_ITEMS = 20;

export interface SummaryFaqFieldsProps {
  /** The product's type, whose suggested questions are offered here; null without a type. */
  productType: { name: string; suggestedFaq: readonly FaqItem[] } | null;
}

/** For someone who may only read them. */
function SharedSummaryFaq() {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const summary = form.watch('summary');
  const faq = form.watch('faq');
  return (
    <div className="flex flex-col gap-4 text-sm">
      <p className="text-muted-foreground text-[13px]">{t('searchCard.sharedAnswers')}</p>
      <div className="flex flex-col gap-1">
        <h3 className="font-medium">{t('fields.summary')}</h3>
        <p className={summary ? 'whitespace-pre-line' : 'text-muted-foreground'}>
          {summary || t('searchCard.noSummary')}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="font-medium">{t('fields.faq')}</h3>
        {faq.length === 0 ? (
          <p className="text-muted-foreground">{t('faq.empty')}</p>
        ) : (
          <dl className="flex flex-col gap-2">
            {faq.map((item, index) => (
              <div key={index} className="rounded-lg border px-3 py-2.5">
                <dt className="font-medium">{item.question}</dt>
                <dd className="text-muted-foreground text-[13px]">{item.answer}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

/**
 * Summary and FAQ: the facts answer and generative engines quote (doc 15), with no card around
 * them. The product type's suggested questions are offered above the product's own and copied in
 * when picked (D-160). On a store's page they're the master's, so a change reaches every store.
 */
export function SummaryFaqFields({ productType }: SummaryFaqFieldsProps) {
  const { readOnly } = useProductScope();
  return readOnly ? <SharedSummaryFaq /> : <EditableSummaryFaq productType={productType} />;
}

function EditableSummaryFaq({ productType }: SummaryFaqFieldsProps) {
  const { t } = useTranslation('products');
  const form = useFormContext<ProductFormInput>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'faq' });
  const summary = form.watch('summary');
  const faq = form.watch('faq');
  const pending = pendingFaqSuggestions(faq, productType);
  const room = MAX_FAQ_ITEMS - fields.length;
  const addSuggestions = (items: readonly FaqItem[]) =>
    append(
      items.slice(0, room).map(({ question, answer }) => ({ question, answer })),
      { shouldFocus: false },
    );
  const { faq: faqError } = form.formState.errors;

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="summary"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('fields.summary')}</FormLabel>
            <FormControl>
              <Textarea rows={3} {...field} />
            </FormControl>
            <FormDescription className="flex justify-between gap-2">
              <span>{t('fields.summaryHint')}</span>
              <span>
                {t('fields.wordCount', {
                  count: summary.trim().split(/\s+/).filter(Boolean).length,
                })}
              </span>
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-semibold">{t('fields.faq')}</h3>
          <p className="text-muted-foreground text-[13px]">
            {productType && pending.length > 0
              ? t('searchCard.suggestedBy', { type: productType.name })
              : t('fields.faqHint')}
          </p>
        </div>
        {productType && pending.length > 0 ? (
          <section
            aria-label={t('faq.suggestedFor', { type: productType.name })}
            className="flex flex-col gap-2"
          >
            <ul className="flex flex-col gap-2">
              {pending.map((item) => (
                <li
                  key={item.question}
                  className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.question}</p>
                    <p className="text-muted-foreground line-clamp-2 text-[13px]">{item.answer}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={room <= 0}
                    aria-label={t('faq.addSuggestionLabel', { question: item.question })}
                    onClick={() => addSuggestions([item])}
                  >
                    {t('faq.addSuggestion')}
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-2">
              {room <= 0 ? (
                <p className="text-muted-foreground text-xs">
                  {t('faq.full', { max: MAX_FAQ_ITEMS })}
                </p>
              ) : (
                <span />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={room <= 0}
                onClick={() => addSuggestions(pending)}
              >
                {t('faq.addAll', { count: pending.length })}
              </Button>
            </div>
          </section>
        ) : null}
        {fields.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('faq.empty')}</p>
        ) : (
          <ol className="flex flex-col gap-3" aria-label={t('fields.faq')}>
            {fields.map((item, index) => (
              <li key={item.id} className="flex flex-col gap-2 rounded-md border p-3">
                <div className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name={`faq.${index}.question`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>{t('faq.question', { n: index + 1 })}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground mt-6"
                    aria-label={t('faq.remove', { n: index + 1 })}
                    onClick={() => remove(index)}
                  >
                    <X aria-hidden />
                  </Button>
                </div>
                <FormField
                  control={form.control}
                  name={`faq.${index}.answer`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('faq.answer', { n: index + 1 })}</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </li>
            ))}
          </ol>
        )}
        {typeof faqError?.message === 'string' ? (
          <p className="text-destructive text-xs">{t(faqError.message)}</p>
        ) : null}
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="text-primary h-auto p-0 text-[13px]"
            disabled={fields.length >= MAX_FAQ_ITEMS}
            onClick={() => append({ question: '', answer: '' })}
          >
            <Plus aria-hidden />
            {t('searchCard.writeOwn')}
          </Button>
        </div>
      </div>
    </div>
  );
}
